import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { call, put, select, takeEvery } from "redux-saga/effects"
import { post, put as putRequest, del } from "api"
import { v4 as uuid } from "uuid"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Types as DataTypes, Creators as updaterCreators } from "model/data"
import { getIntegrations } from "./selectors/apps"
import { find } from "lodash"
import { STATUSES } from "modules/platform/constants"

export const { Types, Creators } = actionCreator("ompnt/modules/platform/", {
  refreshRevision: [],
  createToken: ["appId"],
  deleteToken: ["tokenId"],
  renameToken: ["tokenId", "name"],
  atIntegrationPermission: {
    props: ["account", "location", "legalText"],
    track: true,
    trackType: "alpha-theory-integration-request",
    trackPayload: ({ account }) => ({ account }),
  },
  ezeIntegrationPermission: {
    props: ["account", "location", "legalText"],
    track: true,
    trackType: "eze-integration-request",
    trackPayload: ({ account }) => ({ account }),
  },
  requestIntegration: {
    props: ["account", "id", "name"],
    track: true,
    trackType: "partner-integration-request",
    trackPayload: ({ account, id, name }) => ({ account, id, name }),
  },
  activateApplication: {
    props: ["account", "application", "termsId"],
    track: true,
    trackType: "application-activation",
    trackPayload: ({ account, application, termsId }) => ({ account, application, termsId }),
  },
  aiIntegrationPermission: {
    props: ["account", "location", "legalText"],
    track: true,
    trackType: "ai-permission-granted",
    trackPayload: ({ account }) => ({ account }),
  },
  updateAiPermission: ["isGranted"],
})

const INITIAL_STATE = {
  revision: uuid(),
  atIntegrationRequested: false,
  ezeIntegrationRequested: false,
  aiPermissionGranted: false,
}

function handleRefreshRevision(state) {
  return { ...state, revision: uuid() }
}

function handleATIntegrationPermission(state) {
  return { ...state, atIntegrationRequested: true }
}

function handleEzeIntegrationPermission(state) {
  return { ...state, ezeIntegrationRequested: true }
}

function handleLoadSuccess(state, action) {
  const { resource, data } = action || {}
  const { enabled } = data
  if (resource === "aiPermissionGranted") {
    return { ...state, aiPermissionGranted: enabled }
  }
  return state
}

function handleUpdateAiPermission(state, { isGranted }) {
  return { ...state, aiPermissionGranted: isGranted }
}

export const HANDLERS = {
  [Types.REFRESH_REVISION]: handleRefreshRevision,
  [Types.AT_INTEGRATION_PERMISSION]: handleATIntegrationPermission,
  [Types.EZE_INTEGRATION_PERMISSION]: handleEzeIntegrationPermission,
  [Types.UPDATE_AI_PERMISSION]: handleUpdateAiPermission,
  [DataTypes.LOAD_SUCCESS]: handleLoadSuccess,
}

export default createReducer(INITIAL_STATE, HANDLERS)

function* createToken({ appId }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  try {
    yield call(post, `/api/${account}/applications/${appId}/tokens`)
    yield put(Creators.refreshRevision())
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

function* renameToken({ tokenId, name }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  try {
    yield call(putRequest, `/api/${account}/applications/tokens/${tokenId}`, { name })
    yield put(Creators.refreshRevision())
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

function* deleteToken({ tokenId }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  try {
    yield call(del, `/api/${account}/applications/tokens/${tokenId}`)
    yield put(Creators.refreshRevision())
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

function* atIntegrationPermission({ account }) {
  try {
    yield call(post, `/api/${account}/alphaTheoryPermission`, {})
    yield put(notificationCreators.add("success", null, "Permissions for Alpha Theory have been accepted. Customer success will follow up with the next steps."))
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

function* ezeIntegrationPermission({ account }) {
  try {
    yield call(post, `/api/${account}/ezePermission`, {})
    yield put(notificationCreators.add("success", null, "Permissions for Eze have been accepted. Customer success will follow up with the next steps."))
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

function* aiIntegrationPermission({ account }) {
  try {
    yield call(post, `/api/${account}/aiPermission`, {})
    yield put(notificationCreators.add("success", null, "AI Permissions Granted."))
    yield put(Creators.updateAiPermission(true))
    // Reset the modules/ai-summary resource for ai permissions
    yield put(updaterCreators.resetCollections(["aiPermissionGranted"]))
  } catch {
    yield put(Creators.updateAiPermission(false))
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

function* requestIntegration({ account, id }) {
  try {
    yield call(post, `/api/${account}/accountPartners`, { partnerId: id })
    const { integrations } = yield select(state => ({
      integrations: getIntegrations(state),
    }))
    const updated = {
      ...find(integrations, integration => integration.id === id),
      status: STATUSES.REQUESTED,
    }
    yield put(updaterCreators.updateSuccess("integrations", null, null, updated, { updateCollection: `/api/${account}/accountPartners` }))
    yield put(notificationCreators.add("success", null, "Integration has been requested. Customer success will follow up with the next steps."))
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

function* activateApplication({ account, application, termsId }) {
  try {
    yield call(post, `/api/${account}/activatePartner`, { partnerId: application.id, termsId })
    const { integrations } = yield select(state => ({
      integrations: getIntegrations(state),
    }))
    const updated = {
      ...find(integrations, integration => integration.id === application.id),
      status: STATUSES.ACTIVE,
    }
    yield put(updaterCreators.updateSuccess("integrations", null, null, updated, { updateCollection: `/api/${account}/accountPartners` }))
    yield put(updaterCreators.resetCollections(["accountApplications"]))
    yield put(notificationCreators.add("success", null, `Permissions for ${application.name} have been accepted. Customer success will follow up with the next steps.`))
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occured."))
  }
}

export function* saga() {
  yield takeEvery(Types.CREATE_TOKEN, createToken)
  yield takeEvery(Types.DELETE_TOKEN, deleteToken)
  yield takeEvery(Types.RENAME_TOKEN, renameToken)
  yield takeEvery(Types.AT_INTEGRATION_PERMISSION, atIntegrationPermission)
  yield takeEvery(Types.EZE_INTEGRATION_PERMISSION, ezeIntegrationPermission)
  yield takeEvery(Types.AI_INTEGRATION_PERMISSION, aiIntegrationPermission)
  yield takeEvery(Types.REQUEST_INTEGRATION, requestIntegration)
  yield takeEvery(Types.ACTIVATE_APPLICATION, activateApplication)
}
